import type { HideOptions, Placement, VirtualElement } from '@floating-ui/react-dom';
import { autoUpdate, flip, hide, offset, shift, useFloating } from '@floating-ui/react-dom';
import React, { useContext, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { RicosContext, ToolbarContext, ZIndexContext } from 'ricos-context';

export const FloatingToolbar = ({
  children,
  hideStrategy,
  referenceElement,
  placement,
  boundary,
}: {
  children: React.ReactNode;
  hideStrategy: Exclude<HideOptions['strategy'], undefined>;
  referenceElement: VirtualElement;
  placement: Placement;
  boundary: HTMLElement;
}) => {
  const zIndexService = useContext(ZIndexContext);
  const { portal } = useContext(ToolbarContext);
  const { jsdomMode } = useContext(RicosContext);

  const {
    x,
    y,
    refs: { setReference, setFloating },
    strategy,
    middlewareData,
  } = useFloating({
    placement,
    middleware: [
      flip({
        boundary,
      }),
      shift({
        boundary,
      }),
      hide({
        strategy: hideStrategy,
        ...(hideStrategy !== 'referenceHidden' && { boundary }),
      }),
      offset(8),
    ],

    whileElementsMounted: (referenceEl, floatingEl, update) =>
      autoUpdate(referenceEl, floatingEl, update, {
        animationFrame: true, // this fix issue of reposition when scrolling
      }),
  });

  useEffect(() => {
    setReference(referenceElement);
  }, [setReference, referenceElement]);

  if (!portal) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      ref={setFloating}
      style={{
        position: strategy,
        top: y ?? '',
        left: x ?? '',
        zIndex: zIndexService.getZIndex('TOOLBAR'),
        visibility: jsdomMode
          ? 'visible'
          : middlewareData.hide?.escaped || middlewareData.hide?.referenceHidden || (!x && !y)
          ? 'hidden'
          : 'visible',
      }}
    >
      <div data-hook="ricos-floating-toolbar" tabIndex={-1}>
        {children}
      </div>
    </div>,
    portal
  );
};
